import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

import LanguageSwitcher from "./LanguageSwitcher";
import { useLang } from "@/hooks/useLang";
import { buildLangPath, stripLangPrefix } from "@/utils/routeBuilders";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { lang } = useLang();
  const { t } = useTranslation("navigation");

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
        document.documentElement.style.setProperty("--navbar-height", "56px");
      } else {
        setScrolled(false);
        document.documentElement.style.setProperty("--navbar-height", "72px");
      }
    };

    // Set initial navbar height
    document.documentElement.style.setProperty(
      "--navbar-height",
      scrolled ? "56px" : "72px"
    );

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.documentElement.style.removeProperty("--navbar-height");
    };
  }, [scrolled]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    document.body.style.overflow = !isMenuOpen ? "hidden" : "";
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.style.overflow = "";
  };

  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleHomeClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (stripLangPrefix(location.pathname) !== "/") {
      navigate(buildLangPath(lang, "/"));
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleServiceClick = () => {
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();

    // Get window width for mobile detection
    const isMobile = window.innerWidth < 768;

    if (stripLangPrefix(location.pathname) !== "/") {
      navigate(buildLangPath(lang, "/"));
      setTimeout(() => {
        const contactForm = document.getElementById("contact-form");
        const formSection = document.getElementById("contact-form-section");
        if (isMobile && formSection) {
          const y =
            formSection.getBoundingClientRect().top + window.pageYOffset - 80;
          window.scrollTo({ top: y, behavior: "smooth" });
        } else if (contactForm) {
          const y =
            contactForm.getBoundingClientRect().top + window.pageYOffset - 100;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 200);
    } else {
      const contactForm = document.getElementById("contact-form");
      const formSection = document.getElementById("contact-form-section");
      if (isMobile && formSection) {
        const y =
          formSection.getBoundingClientRect().top + window.pageYOffset - 80;
        window.scrollTo({ top: y, behavior: "smooth" });
      } else if (contactForm) {
        const y =
          contactForm.getBoundingClientRect().top + window.pageYOffset - 100;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }

    setIsMenuOpen(false);
  };

  const serviceLinks = [
    {
      name: t("serviceLinks.implantology.name"),
      href: buildLangPath(lang, "/implantology"),
      description: t("serviceLinks.implantology.description"),
    },
    {
      name: t("serviceLinks.endodontics.name"),
      href: buildLangPath(lang, "/endodontics"),
      description: t("serviceLinks.endodontics.description"),
    },
    {
      name: t("serviceLinks.oral-surgery.name"),
      href: buildLangPath(lang, "/oral-surgery"),
      description: t("serviceLinks.oral-surgery.description"),
    },
    {
      name: t("serviceLinks.periodontology.name"),
      href: buildLangPath(lang, "/periodontology"),
      description: t("serviceLinks.periodontology.description"),
    },
    {
      name: t("serviceLinks.orthodontics.name"),
      href: buildLangPath(lang, "/orthodontics"),
      description: t("serviceLinks.orthodontics.description"),
    },
    {
      name: t("serviceLinks.bonding.name"),
      href: buildLangPath(lang, "/bonding"),
      description: t("serviceLinks.bonding.description"),
    },
    {
      name: t("serviceLinks.prosthetics.name"),
      href: buildLangPath(lang, "/prosthetics"),
      description: t("serviceLinks.prosthetics.description"),
    },
    {
      name: t("serviceLinks.cariesology.name"),
      href: buildLangPath(lang, "/cariesology"),
      description: t("serviceLinks.cariesology.description"),
    },
    {
      name: t("serviceLinks.teeth-whitening.name"),
      href: buildLangPath(lang, "/teeth-whitening"),
      description: t("serviceLinks.teeth-whitening.description"),
    },
    {
      name: t("serviceLinks.dental-tourism.name"),
      href: buildLangPath(lang, "/dental-tourism"),
      description: t("serviceLinks.dental-tourism.description"),
    },
    {
      name: t("serviceLinks.prices.name"),
      href: buildLangPath(lang, "/prices"),
      description: t("serviceLinks.prices.description"),
    },
  ];

  const mainNavLinks = [
    { name: t("nav.home"), href: buildLangPath(lang, "/#home"), onClick: handleHomeClick },
    { name: t("nav.about"), href: buildLangPath(lang, "/#about") },
    { name: t("nav.services"), href: buildLangPath(lang, "/#services"), isServicesMenu: true },
    { name: t("nav.testimonials"), href: buildLangPath(lang, "/#testimonials") },
    { name: t("nav.gallery"), href: buildLangPath(lang, "/#gallery") },
    { name: t("nav.contact"), href: buildLangPath(lang, "/#contact"), onClick: handleContactClick },
  ];

  return (
    <nav
      className={cn(
        "fixed top-0 left-0 w-full transition-all duration-300",
        scrolled
          ? "bg-white/90 shadow-md backdrop-blur-md py-2"
          : "bg-transparent py-4"
      )}
      style={{ zIndex: 9999 }}
    >
      <div className="container mx-auto px-4 md:px-6 relative">
        <div className="flex items-center justify-between">
          <a
            href={buildLangPath(lang, "/#home")}
            className="flex items-center space-x-2"
            onClick={handleHomeClick}
          >
            <img
              src="/images/logo.png"
              alt="Elite Dental Solutions Logo"
              className="w-10 h-10 object-contain"
            />
            <span className="logo-text text-sm md:text-xl">Elite Dental Solutions</span>
          </a>

          <div className="flex items-center space-x-4">
            <LanguageSwitcher className="lg:hidden" />

            <button
              className="lg:hidden text-dental-dark hover:text-dental transition-colors duration-300 relative z-50"
              onClick={toggleMenu}
              aria-label="Toggle mobile menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          <div className="hidden lg:flex items-center space-x-2 xl:space-x-4">
            <NavigationMenu>
              <NavigationMenuList className="font-nav font-bold whitespace-nowrap">
                {mainNavLinks.map((link, index) => {
                  if ((link as any).isServicesMenu) {
                    return (
                      <NavigationMenuItem key={link.name}>
                        <NavigationMenuTrigger className="nav-link relative text-dental-dark hover:text-dental transition-colors duration-300 after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-dental-coral after:transition-all after:duration-300 hover:after:w-full px-2 xl:px-4 py-2 text-sm xl:text-base bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent font-nav font-bold">
                          {t("servicesMenu.trigger")}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                          <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] font-nav">
                            {serviceLinks.map((service) => (
                              <li key={service.name}>
                                <NavigationMenuLink asChild>
                                  <Link
                                    to={service.href}
                                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                    onClick={handleServiceClick}
                                  >
                                    <div className="text-sm font-bold leading-none">{service.name}</div>
                                    <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                                      {service.description}
                                    </p>
                                  </Link>
                                </NavigationMenuLink>
                              </li>
                            ))}
                            <li className="col-span-2 mt-3 border-t pt-3">
                              <NavigationMenuLink asChild>
                                <a
                                  href={buildLangPath(lang, "/#services")}
                                  className="flex items-center justify-center w-full select-none rounded-md p-3 text-sm font-bold leading-none no-underline outline-none transition-colors bg-dental text-white hover:bg-dental-dark focus:bg-dental-dark"
                                >
                                  {t("servicesMenu.allServices")}
                                </a>
                              </NavigationMenuLink>
                            </li>
                          </ul>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    );
                  }

                  const isSectionLink = link.href.includes("#");

                  if (!isSectionLink) {
                    return (
                      <NavigationMenuItem key={link.name}>
                        <Link
                          to={link.href}
                          className={`relative text-dental-dark hover:text-dental font-bold transition-colors duration-300 after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 ${
                            index % 3 === 0
                              ? "after:bg-dental"
                              : index % 3 === 1
                                ? "after:bg-dental-coral"
                                : "after:bg-dental-mint"
                          } after:transition-all after:duration-300 hover:after:w-full px-2 xl:px-4 py-2 text-sm xl:text-base inline-block`}
                        >
                          {link.name}
                        </Link>
                      </NavigationMenuItem>
                    );
                  }

                  return (
                    <NavigationMenuItem key={link.name}>
                      <a
                        href={link.href}
                        className={`relative text-dental-dark hover:text-dental font-bold transition-colors duration-300 after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 ${
                          index % 3 === 0
                            ? "after:bg-dental"
                            : index % 3 === 1
                              ? "after:bg-dental-coral"
                              : "after:bg-dental-mint"
                        } after:transition-all after:duration-300 hover:after:w-full px-2 xl:px-4 py-2 text-sm xl:text-base inline-block`}
                        onClick={(link as any).onClick || undefined}
                      >
                        {link.name}
                      </a>
                    </NavigationMenuItem>
                  );
                })}
              </NavigationMenuList>
            </NavigationMenu>
            <LanguageSwitcher />
          </div>

          <div className="hidden xl:flex items-center space-x-4">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-dental" />
              <span>+359 879 800 430</span>
            </div>
          </div>
        </div>
      </div>

      <div
        className={cn(
          "fixed left-0 right-0 bg-white/95 backdrop-blur-sm lg:hidden transition-all duration-300 ease-in-out overflow-y-auto h-[calc(100vh-var(--navbar-height))]",
          isMenuOpen ? "visible opacity-100" : "invisible opacity-0"
        )}
        style={{
          top: "var(--navbar-height)",
          zIndex: 9998,
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div className="container mx-auto px-4 py-4 flex flex-col space-y-4 pb-20 font-nav font-bold">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-dental" />
            <span>+359 879 800 430</span>
          </div>

          {mainNavLinks.map((link, index) => {
            const isSectionLink = link.href.includes("#");

            if (!isSectionLink) {
              return (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`text-dental-dark hover:text-dental font-bold py-2 border-b ${
                    index % 3 === 0
                      ? "border-dental/30"
                      : index % 3 === 1
                        ? "border-dental-coral/30"
                        : "border-dental-mint/30"
                  } transition-colors duration-300`}
                  onClick={() => closeMenu()}
                >
                  {link.name}
                </Link>
              );
            }

            return (
              <a
                key={link.name}
                href={link.href}
                className={`text-dental-dark hover:text-dental font-bold py-2 border-b ${
                  index % 3 === 0
                    ? "border-dental/30"
                    : index % 3 === 1
                      ? "border-dental-coral/30"
                      : "border-dental-mint/30"
                } transition-colors duration-300`}
                onClick={(e) => {
                  closeMenu();
                  if ((link as any).onClick) {
                    (link as any).onClick(e);
                  }
                }}
              >
                {link.name}
              </a>
            );
          })}

          <div className="py-2 border-b border-dental/30">
            <div className="font-medium text-dental-dark mb-2 font-bold">
              {t("servicesMenu.trigger")}
            </div>
            <div className="pl-4 space-y-2">
              {serviceLinks.map((service) => (
                <Link
                  key={service.name}
                  to={service.href}
                  className="block text-dental-dark/80 hover:text-dental transition-colors duration-300 font-bold"
                  onClick={() => closeMenu()}
                >
                  {service.name}
                </Link>
              ))}
              <a
                href={buildLangPath(lang, "/#services")}
                className="block text-dental-dark/80 hover:text-dental transition-colors duration-300 font-bold"
                onClick={() => closeMenu()}
              >
                {t("servicesMenu.allServices")}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`fixed inset-0 bg-white/95 backdrop-blur-sm transition-transform duration-300 lg:hidden ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ zIndex: 40 }}
      >
        <div className="container mx-auto px-4 py-20">
          <ul className="space-y-6 text-center">
            {mainNavLinks.map((link) => (
              <li key={link.name}>
                {(link as any).isServicesMenu ? (
                  <div className="space-y-4">
                    <span className="text-xl font-bold text-dental-dark">
                      {link.name}
                    </span>
                    <ul className="space-y-3 mt-2">
                      {serviceLinks.map((service) => (
                        <li key={service.name}>
                          <Link
                            to={service.href}
                            className="text-dental-dark/80 hover:text-dental transition-colors duration-300"
                            onClick={() => {
                              closeMenu();
                              handleServiceClick();
                            }}
                          >
                            {service.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <a
                    href={link.href}
                    className="text-xl font-bold text-dental-dark hover:text-dental transition-colors duration-300"
                    onClick={(e) => {
                      if ((link as any).onClick) {
                        (link as any).onClick(e);
                      }
                      closeMenu();
                    }}
                  >
                    {link.name}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
